/**
 * Pure windowing math for the virtualized log list (requirement 4: "a
 * virtualized log of every event kind"). Factored out from the DOM so the
 * range computation is unit-testable without a scrollable element.
 */

export interface VirtualRange {
  readonly startIndex: number;
  readonly endIndex: number; // exclusive
  readonly topPaddingPx: number;
  readonly totalHeightPx: number;
}

/** Computes which rows [startIndex, endIndex) should be rendered for a
 * given scroll position, with `overscan` extra rows on each side so fast
 * scrolling doesn't show gaps before the next render. */
export function computeVirtualRange(
  totalRows: number,
  rowHeightPx: number,
  scrollTopPx: number,
  viewportHeightPx: number,
  overscan = 6,
): VirtualRange {
  if (totalRows <= 0 || rowHeightPx <= 0) {
    return { startIndex: 0, endIndex: 0, topPaddingPx: 0, totalHeightPx: 0 };
  }
  const firstVisible = Math.floor(scrollTopPx / rowHeightPx);
  const visibleCount = Math.ceil(viewportHeightPx / rowHeightPx) + 1;
  const startIndex = Math.max(0, firstVisible - overscan);
  const endIndex = Math.min(totalRows, firstVisible + visibleCount + overscan);
  return {
    startIndex,
    endIndex,
    topPaddingPx: startIndex * rowHeightPx,
    totalHeightPx: totalRows * rowHeightPx,
  };
}

/** The `scrollTop` that pins the last row's bottom edge to the bottom of the
 * viewport — "follow live" mode's own scroll position (owner bug report
 * 2026-09-30: the live activity log always showed the oldest events at the
 * top, never the newest, because the scrollable list started at
 * `scrollTop = 0` and nothing ever moved it). Rows are ordered oldest-first
 * (`buildActivityRowItems`), so scrolling to the bottom — not reversing row
 * order — is what makes the newest events the ones actually on screen.
 * Never negative: a short list (content shorter than one viewport) simply
 * stays pinned to the top, matching the natural "nothing to scroll" case. */
export function computeFollowScrollTop(
  itemCount: number,
  rowHeightPx: number,
  clientHeightPx: number,
): number {
  return Math.max(0, itemCount * rowHeightPx - clientHeightPx);
}
